import { publicPages } from '../../lib/public-site';
import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Compass, Hammer, Folder, BrainCircuit, Settings, HelpCircle, ChevronRight } from "lucide-react";
import { cn } from "@/src/lib/utils";
import { useTenantBrand } from "@/src/lib/tenant-brand";
import { motion, AnimatePresence } from "motion/react";

export default function AppLayout() {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);

  // Detect if we are on the Architect (Build) page — match /build and /build/:id
  const isBuildPage = location.pathname.startsWith("/build");

  // On build page: collapsed by default, only expands via explicit click (not hover)
  // On other pages: hover-to-expand works normally
  const isCollapsed = isBuildPage ? !isManuallyExpanded : !isHovered;
  // White-label brand for the sidebar + browser tab (operator company →
  // neutral). Single source of truth — never a "Sloe" string.
  const brand = useTenantBrand();
  useEffect(() => {
    const isPublicHost = ['www.sloelabs.com', 'sloelabs.com', 'localhost', '127.0.0.1'].includes(window.location.hostname);
    const path = location.pathname.replace(/\/+$/, '') || '/';
    document.title = (isPublicHost && publicPages[path]?.title) || brand.name;
  }, [brand.name, location.pathname]);

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 font-sans antialiased selection:bg-emerald-500/30">

      {/* ─── DYNAMIC SIDEBAR ─── */}
      <aside
        onMouseEnter={() => !isBuildPage && setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); }}
        className={cn(
          "relative border-r border-white/5 bg-[#0a0a0c] flex flex-col flex-shrink-0 z-50 transition-all duration-500 ease-in-out",
          isCollapsed ? "w-[72px]" : "w-64"
        )}
      >
        {/* Logo / Brand Area */}
        <div className={cn(
          "p-6 mb-4 flex flex-col transition-all duration-500",
          isCollapsed ? "items-center px-0" : "items-start"
        )}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">{brand.name.charAt(0).toUpperCase()}</span>
            </div>
            {!isCollapsed && (
              <motion.h1
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-sm font-bold tracking-tighter text-white whitespace-nowrap"
              >
                {brand.name}
              </motion.h1>
            )}
          </div>
          {!isCollapsed && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 mt-1 pl-11">
              <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
              <p className="text-[8px] font-bold tracking-[0.3em] text-slate-400 uppercase">Architectural AI</p>
            </motion.div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-2">
          <NavItem to="/" icon={<Compass />} label="Explore" collapsed={isCollapsed} />
          <NavItem to="/build" icon={<Hammer />} label="The Architect" collapsed={isCollapsed} />
          {/* P2.3 — /sync hidden from nav (power-user feature, re-enable in v2).
              The route stays live in App.tsx (existing bookmarks still resolve);
              only this sidebar entry is hidden. BrainCircuit import is retained
              for trivial re-enable. */}
          {/* <NavItem to="/sync" icon={<BrainCircuit />} label="Brain Sync" collapsed={isCollapsed} /> */}
          <NavItem to="/vault" icon={<Folder />} label="Vault" collapsed={isCollapsed} />
        </nav>

        {/* Bottom Actions */}
        <div className="p-3 border-t border-white/5 space-y-1">
          <button className={cn(
            "flex items-center gap-3 w-full p-3 text-xs font-medium text-slate-400 rounded-xl hover:bg-white/5 transition-colors",
            isCollapsed && "justify-center"
          )}>
            <HelpCircle className="w-4 h-4" />
            {!isCollapsed && <span>Support</span>}
          </button>
          <button className={cn(
            "flex items-center gap-3 w-full p-3 text-xs font-medium text-slate-400 rounded-xl hover:bg-white/5 transition-colors",
            isCollapsed && "justify-center"
          )}>
            <Settings className="w-4 h-4" />
            {!isCollapsed && <span>Settings</span>}
          </button>
        </div>

        {/* Visual Indicator for Collapsed State */}
        {isCollapsed && (
          <button
            onClick={() => isBuildPage ? setIsManuallyExpanded(true) : setIsHovered(true)}
            className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-12 bg-[#0a0a0c] border border-white/5 border-l-0 rounded-r-full flex items-center justify-center hover:w-7 transition-all"
          >
            <ChevronRight className="w-3 h-3 text-slate-500 hover:text-slate-300" />
          </button>
        )}
        {/* Collapse button when manually expanded on build page */}
        {isBuildPage && isManuallyExpanded && (
          <button
            onClick={() => setIsManuallyExpanded(false)}
            className="absolute top-4 right-2 p-1 rounded-lg hover:bg-white/5 transition-colors"
          >
            <ChevronRight className="w-3 h-3 text-slate-500 rotate-180" />
          </button>
        )}
      </aside>

      {/* Main Content Area + persistent legal footer */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-hidden relative">
          <React.Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </React.Suspense>
        </main>
        {/* Plain <a> (not react-router) → full-page load of the static
            /privacy + /terms HTML, which have no client route. */}
        <footer className="shrink-0 border-t border-slate-200 bg-white px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-[11px] text-slate-600">
          <span>SLOE OS by SLOE Labs Inc. · Guild email sending and vertical app calendar connections.</span>
          <a href="/privacy" className="inline-flex min-h-11 items-center px-2 hover:underline">Privacy</a>
          <span aria-hidden="true">·</span>
          <a href="/terms" className="inline-flex min-h-11 items-center px-2 hover:underline">Terms</a>
        </footer>
      </div>
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="flex-1 h-full w-full bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden">
      {/* Centered pulsing elements that look like an AI scanner or initializing grid */}
      <div className="flex flex-col items-center gap-6 z-10">
        <div className="w-16 h-16 rounded-2xl bg-slate-200/50 flex items-center justify-center animate-pulse">
          <div className="w-6 h-6 rounded-md bg-slate-300/50" />
        </div>
        <div className="space-y-3 flex flex-col items-center">
          <div className="h-4 w-32 bg-slate-200/50 rounded-full animate-pulse" />
          <div className="h-3 w-48 bg-slate-200/50 rounded-full animate-pulse delay-75" />
        </div>
      </div>

      {/* Background architectural grid to make it look intentional */}
      <div className="absolute inset-0 z-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-50 pointer-events-none" />
    </div>
  );
}

function NavItem({ to, icon, label, collapsed }: { to: string; icon: React.ReactElement; label: string; collapsed: boolean }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-4 p-3.5 rounded-xl transition-all duration-300 group relative",
          isActive
            ? "bg-emerald-500/10 text-emerald-400"
            : "text-slate-400 hover:bg-white/5 hover:text-slate-200",
          collapsed && "justify-center px-0"
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Icon */}
          <div className={cn("transition-transform duration-300 flex-shrink-0", !collapsed && "group-hover:scale-110")}>
            {React.cloneElement(icon as React.ReactElement<any>, { className: "w-5 h-5", strokeWidth: isActive ? 2.5 : 2 })}
          </div>

          {/* Label */}
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-[13px] font-semibold whitespace-nowrap"
            >
              {label}
            </motion.span>
          )}

          {/* Tooltip for collapsed state */}
          {collapsed && (
            <div className="absolute left-16 px-2 py-1 bg-slate-800 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
              {label}
            </div>
          )}
        </>
      )}
    </NavLink>
  );
}
